import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import EmployeeCard from '@/platform/common/components/EmployeeCard/EmployeeCard';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import PlaceholderValue from '@/platform/common/components/PlaceholderValue/PlaceholderValue';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import { localDate } from '@/platform/lib/utils';
import {
    Badge,
    Button,
    Container,
    FilterBarDropdown,
    FilterBarToggle,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/platform/ui';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import { usePage } from '@inertiajs/react';
import qs from 'qs';
import { useMemo } from 'react';
import CreateOrUpdateModal from './components/CreateOrUpdateModal';
import DocumentLink from './components/DocumentLink';
import Navigation from './components/Navigation';

const CustomerIndexView = () => {
    const { auth, employees, offices } = usePage().props;
    const { filters, updateFilters, isSorted } = useFilters([
        'employees',
        'offices',
    ]);

    const officeOptions = useMemo(() => {
        if (!offices || offices?.length == 0) {
            return [];
        }

        return [
            ...offices,
            {
                id: 'without_office',
                name: 'Geen',
            },
        ];
    }, [offices]);

    const enabled = filters?.entity_ids?.length > 0;

    let numberOfColumns = 8;

    if (filters?.entity_ids?.length > 1) {
        numberOfColumns += 1;
    }

    if (filters?.office_ids?.length > 1) {
        numberOfColumns += 1;
    }

    if (auth?.permissions?.includes('update no-risk')) {
        numberOfColumns += 1;
    }

    return (
        <Container fluid>
            <Navigation />

            <Segment>
                <SegmentHeader
                    header={`Werknemers (${employees?.meta?.total || 0})`}
                    subheader='Stimuleer het aannemen van werknemers met een arbeidsbeperking.'
                >
                    {auth?.permissions?.includes('export no-risk') && (
                        <Button
                            as='a'
                            href={`/customers/${auth?.user?.selected_customer_id}/services/no-risk/employees/exports?${qs?.stringify(
                                {
                                    employed: filters?.employed ? 1 : 0,
                                    expiring: filters?.expiring ? 1 : 0,
                                    entity_ids: filters?.entity_ids || [],
                                    office_ids: filters?.office_ids || [],
                                }
                            )}`}
                            target='_blank'
                            disabled={!enabled}
                        >
                            Exporteren
                        </Button>
                    )}
                </SegmentHeader>

                <SegmentContent>
                    <SearchBarFilter only={['employees']}>
                        <FilterBarDropdown
                            multiple
                            selectAll
                            label={`Entiteiten (${filters?.entity_ids?.length || 0}/${auth?.customer?.entities?.length || 0})`}
                            options={auth.customer?.entities?.map(entity => ({
                                id: entity.id,
                                name: entity.name,
                            }))}
                            value={filters?.entity_ids || []}
                            onChange={value =>
                                updateFilters('entity_ids', value)
                            }
                        />
                        {officeOptions?.length > 0 && (
                            <FilterBarDropdown
                                multiple
                                selectAll
                                label={`Vestiging (${filters?.office_ids?.length || 0}/${officeOptions?.length || 0})`}
                                options={officeOptions || []}
                                value={filters?.office_ids || []}
                                onChange={value =>
                                    updateFilters('office_ids', value)
                                }
                            />
                        )}
                        <FilterBarToggle
                            label='In dienst'
                            checked={!!filters?.employed || false}
                            onChange={checked =>
                                updateFilters('employed', checked ? 1 : 0)
                            }
                        />
                        <FilterBarToggle
                            label='Aflopend'
                            checked={!!filters?.expiring || false}
                            onChange={checked =>
                                updateFilters('expiring', checked ? 1 : 0)
                            }
                        />
                    </SearchBarFilter>

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Medewerker</TableHeaderCell>
                                {filters?.entity_ids?.length > 1 && (
                                    <TableHeaderCell>Entiteit</TableHeaderCell>
                                )}
                                {filters?.office_ids?.length > 1 && (
                                    <TableHeaderCell>Vestiging</TableHeaderCell>
                                )}
                                <TableHeaderCell
                                    sorted={isSorted('employment_date')}
                                    onClick={() =>
                                        updateFilters('sort', 'employment_date')
                                    }
                                >
                                    Datum indienst
                                </TableHeaderCell>
                                <TableHeaderCell>
                                    Datum uitdienst
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    Categorie
                                </TableHeaderCell>
                                <TableHeaderCell
                                    content=''
                                    sorted={isSorted('no_risk_date')}
                                    onClick={() =>
                                        updateFilters('sort', 'no_risk_date')
                                    }
                                >
                                    Start no-risk
                                </TableHeaderCell>
                                <TableHeaderCell>Einde no-risk</TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted('created_at')}
                                    onClick={() =>
                                        updateFilters('sort', 'created_at')
                                    }
                                >
                                    Aangemaakt op
                                </TableHeaderCell>
                                <TableHeaderCell>DGV</TableHeaderCell>
                                {auth?.permissions?.includes(
                                    'update no-risk'
                                ) && <TableHeaderCell collapsing />}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {employees?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={numberOfColumns} />
                            )}

                            {employees?.data?.map(employee => (
                                <TableRow key={employee.id}>
                                    <TableCell>
                                        <EmployeeCard employee={employee} />
                                    </TableCell>
                                    {filters?.entity_ids?.length > 1 && (
                                        <TableCell>
                                            {employee?.entity?.name}
                                        </TableCell>
                                    )}
                                    {filters?.office_ids?.length > 1 && (
                                        <TableCell>
                                            <PlaceholderValue
                                                isPresent={
                                                    employee?.office?.name
                                                }
                                                placeholder='Geen'
                                            >
                                                {employee?.office?.name}
                                            </PlaceholderValue>
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        {localDate(employee.employment_date)}
                                        {employee.employments_count > 1 && (
                                            <Tooltip>
                                                <TooltipTrigger>
                                                    <InformationCircleIcon className='ml-2 size-4' />
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    {`Deze werknemer heeft ${employee.employments_count} dienstverbanden`}
                                                </TooltipContent>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <PlaceholderValue placeholder='Onbepaald'>
                                            {localDate(
                                                employee.unemployment_date
                                            )}
                                        </PlaceholderValue>
                                    </TableCell>
                                    <TableCell textAlign='center'>
                                        <Badge
                                            color={
                                                employee?.period
                                                    ?.reason_colour || 'grey'
                                            }
                                        >
                                            {employee?.period?.reason_label ||
                                                'Geen'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        {localDate(
                                            employee?.period?.start_date
                                        )}
                                        {employee?.period?.is_provisional && (
                                            <Tooltip>
                                                <TooltipTrigger>
                                                    <InformationCircleIcon className='ml-2 size-4' />
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    Deze datums zijn voorlopig
                                                    gebaseerd op de vroegst
                                                    bekende startdatum. De
                                                    no-risk periode kan echter
                                                    eerder beginnen.
                                                </TooltipContent>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <PlaceholderValue placeholder='Onbepaald'>
                                            {localDate(
                                                employee?.period?.end_date
                                            )}
                                        </PlaceholderValue>
                                    </TableCell>
                                    <TableCell>
                                        {localDate(employee?.created_at)}
                                    </TableCell>
                                    <TableCell>
                                        <DocumentLink employee={employee} />
                                    </TableCell>
                                    {auth?.permissions?.includes(
                                        'update no-risk'
                                    ) && (
                                        <TableCell collapsing textAlign='right'>
                                            <CreateOrUpdateModal
                                                period={employee.period}
                                                baseEmployeeId={
                                                    employee?.base_employee_id
                                                }
                                                employments={
                                                    employee.employments
                                                }
                                            />
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>

                <SegmentFooter>
                    <CustomPagination
                        meta={employees?.meta}
                        only={['employees']}
                    />
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default CustomerIndexView;
